import ReactDOMServer from 'react-dom/server'
import 'ag-grid-enterprise'
import { useSelector } from 'react-redux'
import { useVisibilityAwareInterval } from '../../../../common/hooks/use-visibility-aware-interval.ts'
import { selectIdeaListInput } from '../../../../redux/slices/idea-list-input.slice.ts'
import { IdeyaAgGridTable } from '../../../../common/ag-grid/ideya-ag-grid-table.tsx'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ColDef,
  ColumnEvent,
  ColumnResizedEvent,
  GridApi,
  RowStyle,
  SortChangedEvent,
} from 'ag-grid-community'
import {
  FilterChangedEvent,
  GetContextMenuItemsParams,
  GridReadyEvent,
  IGetRowsParams,
  MenuItemDef,
  RowClassParams,
  SortModelItem,
  ValueFormatterParams,
} from 'ag-grid-enterprise'
import {
  getBaseQuery,
  getBaseUrl,
  REQUEST_TIMEOUT_MS,
} from '../../../../apis/api.util.ts'
import {
  IdeaAlert,
  IdeaListItem,
  IdeaListRequest,
  IdeaListResponse,
} from '../../../../apis/idea/idea-api.def.ts'
import type { BaseQueryApi } from '@reduxjs/toolkit/query'
import {
  checkStateColoredNumberCellStyle,
  currencyValueFormatter,
  marketDateTimezoneFormatter,
  percentageValueFormatter,
} from '../../../../utils/formatters.ts'
import { sortMappingIdeaList } from './idea-list-table.def.ts'
import {
  CloseCircleFilled,
  EditOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  InfoCircleFilled,
} from '@ant-design/icons'
import LiquidateIcon from '../../../../assets/icons/idea-list/liquidate.svg'
import LiquidateDarkIcon from '../../../../assets/icons/idea-list/liquidate-dark.svg'
import AutofillIcon from '../../../../assets/icons/idea-list/autofill.svg'
import AutofillDarkIcon from '../../../../assets/icons/idea-list/autofill-dark.svg'
import { useAppSelector } from '../../../../redux/hooks.ts'
import {
  selectTheme,
  selectThemeStyle,
} from '../../../../features/setting/setting.slice.ts'
import { Theme } from '../../../../features/setting/setting.def.ts'
import { IDEA_LIST_COLUMN_STATE_KEY } from '../../../../utils/constants.ts'
import { CustomCellRendererProps } from 'ag-grid-react'
import { IdeaActionColumn } from './idea-action-column.tsx'
import { useIsMobile } from '../../../../common/hooks/use-is-mobile.ts'
import { IdeaDirectionTag } from './idea-direction-tag.tsx'
import { useTranslation } from 'react-i18next'
import { selectUser } from '../../../../features/user/user.slice.ts'
import { IdeaTotalPnlCell } from './idea-total-pnl-cell.tsx'
import dayjs from 'dayjs'
import { getCurrentQuarterRange } from '../../../../utils/time.ts'
import { IsTrialCellRenderer } from '../../../components/is-trial-cell-renderer.tsx'
import {
  getOrderedColumnsFromState,
  isProgrammaticColumnEvent,
} from '../../../../utils/ag-grid-table-helpers.ts'
import { useTutorial } from '../../../../contexts/use-tutorial.ts'
import Tooltip from 'antd/es/tooltip/index'
import { TotalReturnPctDependentHeaderComponent } from './total-return-pct-dependent-header.tsx'
import { IdeaAlertType } from '../../../../enums/idea-list.enum.ts'
import { useToggleKeepFillingMutation } from '../../../../apis/idea/idea-api.api.ts'
import {
  FIRM_ROLE,
  GROUP_ROLE,
  ROLE_ADMIN,
  ROLE_MASTER,
} from '../../../../router/roles.ts'
import { StatusCellRenderer } from '../common/order-status-cell-renderer.tsx'
import { IdeaState } from '../../../../enums/management-input.enum.ts'
import '@/pages/idea-list/components/idea-list-table/idea-list-table.scss'
import { NEW_RELIC, newRelic } from '@/utils/new-relic.ts'
import { blockedFirmIdsCreateEditIdea } from '@/utils/permissions.ts'

function buildIdeaListUrl(request: IdeaListRequest) {
  const params = new URLSearchParams({
    page: String(request.page),
    pageSize: String(request.pageSize ?? ''),
    includeLiquidated: String(request.includeLiquidated),
    firmId: request.firmId ?? '',
    userId: request.userId ?? '',
    region: request.region ?? '',
    netCategory: request.netCategory ?? '',
    orderBy: request.orderBy ?? '',
    orderDirection: request.orderDirection ?? '',
    filterColumn: request.filterColumn ?? '',
    filterText: request.filterText ?? '',
    startDate: request.startDate ?? '',
    endDate: request.endDate ?? '',
  })
  return `/list?${params.toString()}`
}

export interface IdeaListTableRef {
  refresh: () => void
}

interface IdeaListTableProps {
  onRefreshed?: () => void
  onEditIdea: (id: string) => void
  onViewIdea: (id: string) => void
  onViewHistoryIdea: (id: string) => void
  onLiquidateIdea: (id: string) => void
  height: number
}

const baseQuery = getBaseQuery(getBaseUrl('idea'), REQUEST_TIMEOUT_MS)
const pageSize = 25

export const IdeaListTable = forwardRef<IdeaListTableRef, IdeaListTableProps>(
  (
    {
      onRefreshed,
      onEditIdea,
      onViewIdea,
      onViewHistoryIdea,
      onLiquidateIdea,
      height,
    },
    ref
  ) => {
    const ideaListInput = useSelector(selectIdeaListInput)
    const gridApiRef = useRef<GridApi | null>(null)
    const themeStyle = useAppSelector(selectThemeStyle)
    const theme = useAppSelector(selectTheme)
    const isMobile = useIsMobile(768)
    const [activeFilterColumn, setActiveFilterColumn] = useState<string>('')
    const [activeFilterText, setActiveFilterText] = useState<string>('')
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true)
    const [firstPageIdeasCount, setFirstPageIdeasCount] = useState<
      number | null
    >(null)
    const { t } = useTranslation()
    const userInfo = useAppSelector(selectUser)
    const isAdmin =
      userInfo.role === ROLE_ADMIN || userInfo.role === ROLE_MASTER
    const [columnStateReset, setColumnStateReset] = useState<boolean>(false)
    const { setRef } = useTutorial()
    const isGridReady = useRef(false)
    // Rows fetched by refreshInfiniteCache land in later ticks, so count the
    // in-flight getRows calls instead of relying on the refresh call returning.
    const pendingRowRequestsRef = useRef(0)

    const textFilterParams = {
      filterOptions: ['contains'],
      debounceMs: 300,
      buttons: ['reset', 'apply'],
      maxNumConditions: 1,
      closeOnApply: true,
    }

    const mobileColumnOrder = [
      'id',
      'tickerName',
      'pctBP',
      'dailyPnl',
      'totalReturnPct',
      'direction',
    ]

    const [toggleKeepFilling, { isSuccess: isToggleKeepFillingSuccess }] =
      useToggleKeepFillingMutation()

    const columnDefs = useMemo<ColDef[]>(() => {
      const endDate = dayjs(ideaListInput.dateRange[1]).format('YYYY-MM-DD')
      const isQuarterEnd = endDate === getCurrentQuarterRange()[1]
      const isFutureOrToday =
        dayjs(endDate).isSame(dayjs(), 'day') ||
        dayjs(endDate).isAfter(dayjs(), 'day')
      const isEndDateNull = endDate == 'Invalid Date'
      const isHistorical = !isQuarterEnd && !isFutureOrToday && !isEndDateNull

      const baseColumns: ColDef[] = [
        {
          headerName: t('idea_list.idea_table.columns.alert'),
          field: 'alerts',
          suppressHeaderMenuButton: true,
          sortable: false,
          cellRenderer: (props: CustomCellRendererProps) => {
            if (!Array.isArray(props.value)) return <></>

            const alertTypeMap = {
              [IdeaAlertType.Error]: {
                icon: (
                  <CloseCircleFilled
                    style={{ color: themeStyle.agGridTableRed }}
                  />
                ),
              },
              [IdeaAlertType.Info]: {
                icon: (
                  <InfoCircleFilled style={{ color: themeStyle.colorInfo }} />
                ),
              },
              [IdeaAlertType.Warning]: {
                icon: (
                  <ExclamationCircleFilled
                    style={{ color: themeStyle.colorWarning }}
                  />
                ),
              },
            }

            const alert = [...props.value].sort(
              (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime()
            )[0]

            if (!alert) return <></>

            const { icon } =
              alertTypeMap[alert.type as keyof typeof alertTypeMap] || {}

            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Tooltip
                  title={
                    <>{alert.title}: Check the Action log for more details.</>
                  }
                  color={theme === Theme.Light ? 'white' : ''}
                  styles={{
                    body: {
                      color: theme === Theme.Light ? themeStyle.colorText : '',
                    },
                  }}
                >
                  {icon}
                </Tooltip>
              </div>
            )
          },
          maxWidth: 90,
          pinned: isMobile ? null : 'left',
        },
        {
          headerName: '',
          suppressHeaderMenuButton: true,
          field: 'id',
          cellRenderer: (props: CustomCellRendererProps) => {
            if (props.value !== undefined) {
              return (
                <IdeaActionColumn
                  data={props.data}
                  onEditIdea={onEditIdea}
                  onViewIdea={onViewIdea}
                />
              )
            }
            return <></>
          },
          cellRendererParams: {
            onEditClick: onEditIdea,
            onHistoryClick: onViewIdea,
          },
          maxWidth: 110,
          minWidth: 105,
          pinned: isMobile ? null : 'left',
        },
        {
          headerName: t('idea_list.idea_table.columns.ticker_code'),
          field: 'tickerCode',
          filter: 'agTextColumnFilter',
          filterParams: textFilterParams,
          pinned: isMobile ? null : 'left',
        },
        {
          headerName: t(
            'idea_list.idea_modal.idea_action_log.table.columns.order_status'
          ),
          field: 'lastFillingState',
          cellRenderer: StatusCellRenderer,
        },
        {
          headerName: t('idea_list.idea_table.columns.fill_status'),
          field: 'lastFilledResult',
        },
        {
          headerName: t('idea_list.idea_table.columns.ticker_name'),
          field: 'tickerName',
          filter: 'agTextColumnFilter',
          filterParams: textFilterParams,
        },
        {
          headerName: t('idea_list.idea_table.columns.ticker_legal_name'),
          field: 'tickerLegalName',
        },
        {
          headerName: t('idea_list.idea_table.columns.direction'),
          field: 'direction',
          filter: 'agTextColumnFilter',
          filterParams: textFilterParams,
          cellRenderer: (props: CustomCellRendererProps) => {
            if (props.value !== undefined) {
              return <IdeaDirectionTag direction={props.data.direction} />
            }
            return <></>
          },
        },
        {
          headerName: t('idea_list.idea_table.columns.daily_return_pct'),
          field: 'dailyReturnPct',
          type: 'numberColumn',
          valueFormatter: (params: ValueFormatterParams) =>
            percentageValueFormatter(params),
          cellStyle: checkStateColoredNumberCellStyle,
        },
        {
          headerName: t('idea_list.idea_table.columns.daily_pnl'),
          field: 'dailyPnl',
          type: 'numberColumn',
          cellStyle: checkStateColoredNumberCellStyle,
          valueFormatter: (params: ValueFormatterParams) =>
            currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
        },
        {
          headerName: t('idea_list.idea_table.columns.quarter_pnl'),
          field: 'quarterPnl',
          type: 'numberColumn',
          valueFormatter: (params: ValueFormatterParams) =>
            isHistorical
              ? '-'
              : currencyValueFormatter(params, {
                  decimal: 0,
                  currency: 'USD',
                }),
          cellStyle: isHistorical
            ? undefined
            : checkStateColoredNumberCellStyle,
        },
        {
          headerName: t('idea_list.idea_table.columns.total_return_pct'),
          field: 'totalReturnPct',
          type: 'numberColumn',
          valueFormatter: (params: ValueFormatterParams) =>
            percentageValueFormatter(params),
          cellStyle: checkStateColoredNumberCellStyle,
          sortable: true,
          headerComponentParams: {
            innerHeaderComponent: TotalReturnPctDependentHeaderComponent,
          },
        },
        {
          headerName: t('idea_list.idea_table.columns.current_total_pnl'),
          field: 'currentTotalPnl',
          type: 'numberColumn',
          cellStyle: checkStateColoredNumberCellStyle,
          cellRenderer: IdeaTotalPnlCell,
          cellRendererParams: {
            onButtonClick: (rowData: IdeaListItem) => {
              onViewHistoryIdea(rowData.id)
            },
          },
        },
        {
          headerName: t('idea_list.idea_table.columns.cumulative_pnl'),
          field: 'cumulativePnl',
          type: 'numberColumn',
          valueFormatter: (params: ValueFormatterParams) =>
            currencyValueFormatter(params, {
              decimal: 0,
              currency: 'USD',
            }),
          cellStyle: checkStateColoredNumberCellStyle,
        },
        {
          headerName: t('idea_list.idea_table.columns.avg_cost'),
          field: 'avgCost',
          type: 'numberColumn',
          valueFormatter: currencyValueFormatter,
        },
        {
          headerName: t('idea_list.idea_table.columns.current_market_value'),
          field: 'currentMarketValue',
          type: 'numberColumn',
          valueFormatter: (params: ValueFormatterParams) =>
            currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
        },
        {
          headerName: t('idea_list.idea_table.columns.pct_gmv'),
          field: 'pctGMV',
          type: 'numberColumn',
          valueFormatter: (params: ValueFormatterParams) =>
            isHistorical ? '-' : percentageValueFormatter(params),
          cellStyle: isHistorical
            ? undefined
            : checkStateColoredNumberCellStyle,
        },
        {
          headerName: t('idea_list.idea_table.columns.pct_bp'),
          field: 'pctBP',
          type: 'numberColumn',
          valueFormatter: (params: ValueFormatterParams) =>
            isHistorical ? '-' : percentageValueFormatter(params),
          cellStyle: isHistorical
            ? undefined
            : checkStateColoredNumberCellStyle,
        },
        {
          headerName: t('idea_list.idea_table.columns.shares'),
          field: 'shares',
          type: 'numberColumn',
        },
        {
          headerName: t('idea_list.idea_table.columns.target_market_value'),
          field: 'targetMarketValue',
          type: 'numberColumn',
          valueFormatter: (params: ValueFormatterParams) =>
            isHistorical
              ? '-'
              : currencyValueFormatter(params, {
                  decimal: 0,
                  currency: 'USD',
                }),
        },
        {
          headerName: t('idea_list.idea_table.columns.contributor'),
          field: 'contributor',
          filter: 'agTextColumnFilter',
          filterParams: textFilterParams,
        },
        {
          headerName: t('idea_list.idea_table.columns.firm_name'),
          field: 'firmName',
          filter: 'agTextColumnFilter',
          filterParams: textFilterParams,
        },
        {
          headerName: t('idea_list.idea_table.columns.state'),
          field: 'state',
          filter: 'agTextColumnFilter',
          filterParams: textFilterParams,
        },
        {
          headerName: t('idea_list.idea_table.columns.duration'),
          field: 'duration',
          type: 'numberColumn',
          valueFormatter: (params: ValueFormatterParams) =>
            isHistorical ? '-' : params.value,
        },
        {
          headerName: t('idea_list.idea_table.columns.created_at'),
          field: 'createdAt',
          type: ['dateColumn'],
          valueFormatter: (params: ValueFormatterParams) =>
            marketDateTimezoneFormatter(params, { showTimezone: true }),
        },
        {
          headerName: t('idea_list.idea_table.columns.closed_at'),
          field: 'closedAt',
          type: ['dateColumn'],
          valueFormatter: (params: ValueFormatterParams) =>
            marketDateTimezoneFormatter(params, { showTimezone: true }),
        },
      ]

      // Only show account type for internal admin users
      if (
        userInfo.role === 'Admin' ||
        userInfo.role === 'Master' ||
        userInfo.role === 'SuperViewer'
      ) {
        baseColumns.push({
          headerName: t('idea_list.idea_table.columns.account_type'),
          field: 'isTrial',
          cellRenderer: IsTrialCellRenderer,
        })
      }

      // Special column order for mobile view
      if (isMobile) {
        const firstCols = mobileColumnOrder
          .map((field) => baseColumns.find((col) => col.field === field))
          .filter(Boolean) as ColDef[]
        const restCols = baseColumns.filter(
          (col) => !mobileColumnOrder.includes(col.field ?? '')
        )
        return [...firstCols, ...restCols]
      }

      if (columnStateReset) {
        setColumnStateReset(false)
        return baseColumns
      }

      const savedState = localStorage.getItem(IDEA_LIST_COLUMN_STATE_KEY)
      const orderedColumns = getOrderedColumnsFromState(
        savedState,
        baseColumns,
        gridApiRef.current
      )
      if (orderedColumns) return orderedColumns

      return baseColumns
    }, [
      userInfo,
      ideaListInput,
      onEditIdea,
      onViewIdea,
      onViewHistoryIdea,
      isMobile,
      t,
      columnStateReset,
    ])

    const defaultColDef = useMemo<ColDef>(
      () => ({
        sortable: true,
        resizable: true,
        initialWidth: 200,
        filter: false,
        mainMenuItems: (params) => {
          return params.defaultItems.map((item) => {
            if (item === 'resetColumns') {
              return {
                name: 'Reset Columns',
                action: () => {
                  setColumnStateReset(true)
                  localStorage.removeItem(IDEA_LIST_COLUMN_STATE_KEY)
                  gridApiRef.current?.resetColumnState()
                  newRelic.recordCustomEvent('IdeaListTableCustomizeAction', {
                    element: NEW_RELIC.LIST_CUSTOMIZE_ACTION.resetColumn,
                    event: `${userInfo.email}`,
                  })
                },
              }
            }
            return item
          })
        },
      }),
      []
    )

    const convertSortModel = (sortModel: SortModelItem[]) => {
      if (sortModel.length > 0) {
        const orderBy = sortMappingIdeaList[sortModel[0].colId]
        const order = sortModel[0].sort.toUpperCase()

        return [orderBy, order]
      }
      return null
    }

    const datasource = useMemo(
      () => ({
        getRows: async (params: IGetRowsParams) => {
          const [region, netCategory] = ideaListInput.category
            ? ideaListInput.category.split('-').map((s) => s.trim())
            : ['', '']

          pendingRowRequestsRef.current += 1
          try {
            const sortResult = convertSortModel(params.sortModel)
            const orderBy = sortResult ? sortResult[0] : ''
            const order = sortResult ? sortResult[1] : ''
            const isInternalUser = userInfo.role !== ''

            const filterColumn = activeFilterColumn
              ? sortMappingIdeaList[activeFilterColumn] || activeFilterColumn
              : ''
            const filterText = activeFilterText || ''

            const isManagerOrCompliance =
              userInfo.groupRole === GROUP_ROLE.ROLE_MANAGER ||
              userInfo.firmRole === FIRM_ROLE.ROLE_COMPLIANCE

            const userId =
              ideaListInput.onlyMyIdea &&
              !isInternalUser &&
              !isManagerOrCompliance
                ? userInfo.sub
                : ideaListInput.contributor || ''

            const rawStartDate = ideaListInput.dateRange[0]
            const rawEndDate = ideaListInput.dateRange[1]

            const startDate = dayjs(rawStartDate).isValid()
              ? dayjs(rawStartDate).format('YYYY-MM-DD')
              : undefined

            const endDate = dayjs(rawEndDate).isValid()
              ? dayjs(rawEndDate).format('YYYY-MM-DD')
              : undefined

            const request: IdeaListRequest = {
              page: Math.floor(params.startRow / pageSize) + 1,
              pageSize: pageSize,
              includeLiquidated: ideaListInput.showLiquidatedIdeas,
              firmId: ideaListInput.firm || '',
              userId: userId,
              region: region || '',
              netCategory: netCategory || '',
              orderBy: orderBy,
              orderDirection: order,
              startDate: startDate,
              endDate: endDate,
              filterColumn,
              filterText,
            }

            const result = await baseQuery(
              {
                url: buildIdeaListUrl(request),
                method: 'GET',
              },
              {} as BaseQueryApi,
              {}
            )

            const dataResult = result as { data?: IdeaListResponse }

            if (dataResult && dataResult.data && dataResult.data.data) {
              const ideas = dataResult.data.data.ideas
              const lastRow =
                ideas.length < pageSize
                  ? params.startRow + ideas.length
                  : undefined

              if (params.startRow === 0) {
                setFirstPageIdeasCount(ideas.length)
              }
              params.successCallback(ideas, lastRow)
            } else {
              params.failCallback()
            }
          } catch {
            params.failCallback()
          } finally {
            pendingRowRequestsRef.current -= 1
            setIsInitialLoad(false)
          }
        },
      }),

      [
        activeFilterColumn,
        activeFilterText,
        ideaListInput.showLiquidatedIdeas,
        ideaListInput.firm,
        ideaListInput.contributor,
        ideaListInput.category,
        ideaListInput.onlyMyIdea,
        ideaListInput.dateRange,
        userInfo.sub,
        userInfo.role,
        userInfo.groupRole,
        userInfo.firmRole,
      ]
    )

    const handleGridReady = useCallback(
      (params: GridReadyEvent) => {
        gridApiRef.current = params.api
        const savedState = localStorage.getItem(IDEA_LIST_COLUMN_STATE_KEY)
        if (savedState) {
          const state = JSON.parse(savedState)
          gridApiRef.current.applyColumnState({
            state: state,
            applyOrder: true,
          })
        }
        params.api.setGridOption('datasource', datasource)
        isGridReady.current = true

        // Use querySelector to get the Daily PnL column header
        setTimeout(() => {
          const headerCell = document.querySelector('[col-id="tickerCode"]')
          if (headerCell) {
            const dailyPnlRef = { current: headerCell as HTMLElement }
            setRef('tableColumn', dailyPnlRef)
          }
        }, 100)
      },
      [setRef, datasource]
    )

    const handleColumnChanged = useCallback(
      (event: ColumnEvent) => {
        // Ignore programmatic changes (e.g. applyColumnState on restore) so
        // they don't overwrite the user's saved layout.
        if (isProgrammaticColumnEvent(event.source)) return
        if (isInitialLoad || !gridApiRef.current) return
        const state = gridApiRef.current.getColumnState()
        localStorage.setItem(IDEA_LIST_COLUMN_STATE_KEY, JSON.stringify(state))
        newRelic.recordCustomEvent('IdeaListTableCustomizeAction', {
          element: NEW_RELIC.LIST_CUSTOMIZE_ACTION.columnChange,
          event: `${event.source} - ${event.type} - ${userInfo.email}`,
        })
      },
      [isInitialLoad]
    )

    const handleColumnResized = useCallback(
      (event: ColumnResizedEvent) => {
        if (
          isInitialLoad ||
          !event.finished ||
          !gridApiRef.current ||
          event.source !== 'uiColumnResized'
        )
          return

        const state = gridApiRef.current.getColumnState()
        localStorage.setItem(IDEA_LIST_COLUMN_STATE_KEY, JSON.stringify(state))
        newRelic.recordCustomEvent('IdeaListTableCustomizeAction', {
          element: NEW_RELIC.LIST_CUSTOMIZE_ACTION.columnResize,
          event: `${event.source} - ${event.type} - ${userInfo.email}`,
        })
      },
      [isInitialLoad]
    )

    const handleSortChanged = useCallback(
      (event: SortChangedEvent) => {
        // Ignore programmatic sort changes so a restore/refetch can't persist
        // an empty sort over the user's choice.
        if (isProgrammaticColumnEvent(event.source)) return
        if (isInitialLoad || !gridApiRef.current) return
        const state = gridApiRef.current.getColumnState()
        localStorage.setItem(IDEA_LIST_COLUMN_STATE_KEY, JSON.stringify(state))
        newRelic.recordCustomEvent('IdeaListTableCustomizeAction', {
          element: NEW_RELIC.LIST_CUSTOMIZE_ACTION.sortChange,
          event: `${event.source} - ${event.type} - ${userInfo.email}`,
        })
      },
      [isInitialLoad]
    )

    const handleFilterChanged = useCallback(
      (event: FilterChangedEvent) => {
        if (!event.api) return

        const filterModel = event.api.getFilterModel()
        const filterColumns = Object.keys(filterModel)

        if (filterColumns.length === 0) {
          setActiveFilterColumn('')
          setActiveFilterText('')
          return
        }

        if (filterColumns.length > 1) {
          const newFilter = filterColumns.find(
            (col) => col !== activeFilterColumn
          )

          if (newFilter) {
            const newFilterModel = { [newFilter]: filterModel[newFilter] }
            event.api.setFilterModel(newFilterModel)

            setActiveFilterColumn(newFilter)
            setActiveFilterText(filterModel[newFilter].filter || '')
            return
          }
        }

        if (filterColumns.length === 1) {
          const singleFilterColumn = filterColumns[0]
          setActiveFilterColumn(singleFilterColumn)
          setActiveFilterText(filterModel[singleFilterColumn].filter || '')
        }

        // Get the most recently changed column
        const changedColumn = filterColumns[filterColumns.length - 1]

        if (changedColumn && filterModel[changedColumn]) {
          if (changedColumn !== activeFilterColumn && activeFilterColumn) {
            const newFilterModel = {
              [changedColumn]: filterModel[changedColumn],
            }
            event.api.setFilterModel(newFilterModel)
          }

          setActiveFilterColumn(changedColumn)
          setActiveFilterText(filterModel[changedColumn].filter || '')
          return
        }
      },
      [activeFilterColumn]
    )

    // Expose refresh method to parent
    useImperativeHandle(ref, () => ({
      refresh: () => {
        if (gridApiRef.current) {
          gridApiRef.current.refreshInfiniteCache()
          onRefreshed?.()
        }
      },
    }))

    useEffect(() => {
      if (firstPageIdeasCount === null) return
      if (firstPageIdeasCount === 0) {
        gridApiRef.current?.showNoRowsOverlay()
      } else {
        gridApiRef.current?.hideOverlay()
      }
    }, [firstPageIdeasCount])

    // Refresh when filters change
    useEffect(() => {
      if (gridApiRef.current) {
        onRefreshed?.()
      }
    }, [
      ideaListInput.category,
      ideaListInput.firm,
      ideaListInput.contributor,
      ideaListInput.dateRange,
      ideaListInput.onlyMyIdea,
      ideaListInput.showLiquidatedIdeas,
      ideaListInput.onlyPremiumLeague,
      onRefreshed,
      isToggleKeepFillingSuccess,
    ])

    useEffect(() => {
      if (!isGridReady.current || !gridApiRef.current) return
      gridApiRef.current.setGridOption('datasource', datasource)
    }, [datasource])

    // Periodically refresh all currently loaded data, skip when page is not
    // visible or when a previous fetch is still running
    useVisibilityAwareInterval(
      () => {
        if (gridApiRef.current) {
          gridApiRef.current.refreshInfiniteCache()
          onRefreshed?.()
        }
      },
      Number(import.meta.env.VITE_GET_IDEA_LIST_INTERVAL),
      () => pendingRowRequestsRef.current > 0
    )

    const getContextMenuItems = useCallback(
      (params: GetContextMenuItemsParams) => {
        const items: MenuItemDef[] = []

        // Only show Edit if canEdit is true
        if (
          params.node &&
          params.node.data &&
          params.node.data.canEdit &&
          !blockedFirmIdsCreateEditIdea.includes(userInfo.firmId)
        ) {
          items.push({
            name: t('idea_list.idea_table.edit'),
            action: () => {
              onEditIdea(params.node?.data.id)
            },
            icon: ReactDOMServer.renderToString(
              <EditOutlined style={{ color: themeStyle.colorText }} />
            ),
          })
        }

        items.push({
          name: t('idea_list.idea_table.view'),
          action: () => {
            if (params.node) {
              onViewIdea(params.node.data.id)
            }
          },
          icon: ReactDOMServer.renderToString(
            <EyeOutlined style={{ color: themeStyle.colorText }} />
          ),
        })

        if (
          params.node &&
          params.node.data &&
          params.node.data.canEdit &&
          !blockedFirmIdsCreateEditIdea.includes(userInfo.firmId)
        ) {
          items.push({
            name: t('idea_list.idea_table.liquidate'),
            action: () => {
              if (params.node) {
                onLiquidateIdea(params.node.data.id)
              }
            },
            icon: ReactDOMServer.renderToString(
              theme === Theme.Dark ? <LiquidateDarkIcon /> : <LiquidateIcon />
            ),
          })
          if (params.node.data.lastFillingState === 'UnFilled' && !isAdmin) {
            items.push({
              name: `${params.node.data.keepFilling === false ? 'Activate' : 'Deactivate'} Auto-fill`,
              action: () => {
                toggleKeepFilling({ id: params?.node?.data.id })
                if (gridApiRef.current) {
                  console.log('reached here')
                  gridApiRef.current.refreshInfiniteCache()
                }
                onRefreshed?.()
              },
              icon: ReactDOMServer.renderToString(
                theme === Theme.Dark ? <AutofillDarkIcon /> : <AutofillIcon />
              ),
            })
          }
        }

        return items
      },
      [toggleKeepFilling]
    )

    const getRowStyle = (params: RowClassParams) => {
      const style: RowStyle = {}

      if (params.data?.lastFillingState === 'Ignore') {
        style.fontStyle = 'italic'
        style.fontWeight = 400
      } else if (params.data?.lastFillingState === 'Filled') {
        style.fontWeight = 500
      }

      if (
        params.data?.state === IdeaState.PreLiquidate ||
        params.data?.state === IdeaState.Liquidate ||
        params.data?.state === IdeaState.Ignored
      ) {
        style.color = themeStyle.colorTextDisabled ?? ''
      }

      if (
        params.data &&
        Array.isArray(params.data.alerts) &&
        params.data.alerts.length > 0
      ) {
        const latestAlert = [...params.data.alerts]
          .filter((alert: IdeaAlert) => alert.type && alert.timestamp)
          .sort(
            (a, b) =>
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          )[0]

        if (!latestAlert) return Object.keys(style).length ? style : undefined
        const bgMap: Record<string, string> = {
          [IdeaAlertType.Info]: themeStyle.colorInfoBg ?? '',
          [IdeaAlertType.Warning]: themeStyle.colorWarningBg ?? '',
          [IdeaAlertType.Error]: themeStyle.colorError ?? '',
        }

        style.backgroundColor = bgMap[latestAlert.type] ?? themeStyle.colorError
      }

      return Object.keys(style).length ? style : undefined
    }

    return (
      <IdeyaAgGridTable
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowModelType={'infinite'}
        cacheBlockSize={pageSize}
        rowBuffer={3}
        onGridReady={handleGridReady}
        onColumnMoved={handleColumnChanged}
        onColumnPinned={handleColumnChanged}
        onColumnVisible={handleColumnChanged}
        onColumnResized={handleColumnResized}
        onSortChanged={handleSortChanged}
        getContextMenuItems={getContextMenuItems}
        onFilterChanged={handleFilterChanged}
        getRowStyle={getRowStyle}
        height={isMobile ? 400 : height}
        loading={isInitialLoad}
        className={'idea-list-table'}
      />
    )
  }
)
